import { isEmailShaped } from '@/features/application/types';
import type { CheckInFactorName, CheckInFactors } from '@/lib/kioskSession';

/**
 * What the check-in form checks before it sends a lookup [ISS-147].
 *
 * The rules are the prototype's own `VALIDATORS.lookup`, one per field it marks: an email that has the
 * shape of one, a last name that is not blank, both halves of the date of birth, and exactly four digits for
 * the SSN. They are SHAPE rules only. Whether the four identify a person is the api's four-factor match
 * [0032] and nothing here decides it.
 *
 * Checking the shape here is not a second copy of the api's contract for its own sake: a request the api
 * will refuse used to come back as a failure the kiosk read as an outage, and an outage lands on the neutral
 * hand-off, which promises a representative nobody was told about.
 */

/** The four things the form asks for. The date of birth is ONE answer in two halves, with one message. */
export const CHECK_IN_FIELD = {
  email: 'email',
  lastName: 'lastName',
  birthday: 'birthday',
  ssnLast4: 'ssnLast4',
} as const;
export type CheckInField = (typeof CHECK_IN_FIELD)[keyof typeof CHECK_IN_FIELD];

/** Exactly four digits, leading zeros included — `0123` is a real last-4. */
export const SSN_LAST4_LENGTH = 4;

const SSN_LAST4_SHAPE = new RegExp(`^[0-9]{${String(SSN_LAST4_LENGTH)}}$`);

/** The field a factor belongs to — the two date halves share one. */
export function fieldOf(factor: CheckInFactorName): CheckInField {
  return factor === 'birthDay' || factor === 'birthMonth' ? CHECK_IN_FIELD.birthday : factor;
}

/** Every field that would not be sent as it stands, in the order the form draws them. */
export function invalidFields(factors: CheckInFactors): readonly CheckInField[] {
  const invalid: CheckInField[] = [];
  // The profile page's own rule, one copy of it [`isEmailShaped`]: looser than the api's `@IsEmail()` on
  // purpose, and what slips through here comes back from the api as a named refusal.
  if (!isEmailShaped(factors.email)) invalid.push(CHECK_IN_FIELD.email);
  if (factors.lastName.trim() === '') invalid.push(CHECK_IN_FIELD.lastName);
  if (factors.birthMonth === '' || factors.birthDay === '') invalid.push(CHECK_IN_FIELD.birthday);
  if (!SSN_LAST4_SHAPE.test(factors.ssnLast4)) invalid.push(CHECK_IN_FIELD.ssnLast4);
  return invalid;
}
